import { Fragment, useId, useRef, useState, type ReactNode } from "react";
import { copyText } from "../api";
import { formatText, useTexts } from "../texts";
import { ChatIcon } from "./icons/Icon";
import { useToast } from "./kit";
import { Sheet } from "./Sheet";
import styles from "./pieces.module.css";

// Piezas nuevas del catálogo 1.14.7 para el Admin (y para todo Bheli): C40 estado de página, C50 cadena ordenada, C51
// dato que se muestra una sola vez, C52 código copiable, C53 bloque de código, C54 campo de contraseña y C55 estado de un
// servicio. Medidas y data-part de la referencia Componentes.dc.html.

/** C55 · si algo técnico funciona ahora: funciona · revisar · falló · apagado, con punto y palabra (no solo color). */
export type ServiceTone = "ok" | "warn" | "fail" | "off";

export function ServiceState({ tone, children }: { tone: ServiceTone; children: ReactNode }) {
  return (
    <span className={`${styles.state} ${styles[tone]}`} data-part="estado-servicio">
      <i className={styles.dot} aria-hidden="true" />
      {children}
    </span>
  );
}

/**
 * C40 · la pantalla entera no se puede mostrar (no existe, sin permiso, no disponible o error), dentro del marco y con
 * su encabezado. Con una referencia (C52) para dictar a soporte cuando es un error inesperado.
 */
export function PageState({
  title,
  line,
  action,
  reference,
  head,
}: {
  title: string;
  line?: string;
  action?: ReactNode;
  reference?: string | null;
  head?: ReactNode;
}) {
  return (
    <>
      {head}
      <div className={styles.page} role="status" data-part="estado-pagina">
        <strong>{title}</strong>
        {line ? <p>{line}</p> : null}
        {reference ? <CopyCode code={reference} /> : null}
        {action ? <div className={styles.pageAction}>{action}</div> : null}
      </div>
    </>
  );
}

/** C52 · identificador que se dicta o se pega en soporte: monoespaciado y su botón de copiar adentro. */
export function CopyCode({ code }: { code: string }) {
  const { L } = useTexts();
  const toast = useToast();
  const text = useRef<HTMLElement>(null);
  return (
    <span className={styles.code} data-part="codigo">
      <code ref={text}>{code}</code>
      <button
        type="button"
        aria-label={formatText(L("ADM_COPY_CODE"), code)}
        title={formatText(L("ADM_COPY_CODE"), code)}
        data-part="copiar"
        onClick={() =>
          void copyText(code).then((done) => {
            if (done) toast.show(L("ADM_COPIED"));
            else {
              selectContents(text.current);
              toast.show(L("ADM_COPY_FAILED"));
            }
          })
        }
      >
        <ChatIcon name="copy" />
      </button>
    </span>
  );
}

/** Deja el texto seleccionado para Ctrl + C cuando el navegador no deja copiar (como C46). */
function selectContents(element: HTMLElement | null) {
  if (!element) return;
  const range = document.createRange();
  range.selectNodeContents(element);
  const selection = window.getSelection();
  selection?.removeAllRanges();
  selection?.addRange(range);
}

/**
 * C53 · JSON de una petición, respuesta o contexto técnico: con sangría, Unicode legible, sin partir líneas y con su
 * desplazamiento lateral dentro de la caja. Solo donde sirve para la acción de la pantalla (D7, V16).
 */
export function CodeBlock({ title, value }: { title: string; value: unknown }) {
  const { L } = useTexts();
  const toast = useToast();
  const text = useRef<HTMLPreElement>(null);
  const content = typeof value === "string" ? prettyJson(value) : JSON.stringify(value, null, 2);
  return (
    <div className={styles.block} data-part="bloque-codigo">
      <div className={styles.blockHead}>
        <span data-part="titulo">{title}</span>
        <button
          type="button"
          className="tm-btn secondary"
          data-part="copiar"
          onClick={() =>
            void copyText(content).then((done) => {
              if (done) toast.show(L("ADM_COPIED"));
              else {
                selectContents(text.current);
                toast.show(L("ADM_COPY_FAILED"));
              }
            })
          }
        >
          <ChatIcon name="copy" />
          {L("ADM_COPY")}
        </button>
      </div>
      <pre ref={text} className={styles.blockCode} tabIndex={0} data-part="codigo">
        {content}
      </pre>
    </div>
  );
}

/** Un texto que es JSON se muestra con sangría; si no lo es, tal cual. */
function prettyJson(value: string): string {
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return value;
  }
}

/**
 * C51 · un secreto que Bheli genera y no vuelve a mostrar (clave temporal, enlace de un solo uso). Nunca en la URL ni en el
 * navegador: quien lo muestra lo tiene solo en memoria; al recargar, la pantalla dice que ya se mostró.
 */
export function OnceSecret({
  label,
  value,
  help,
  copiedMessage,
}: {
  label: string;
  value: string;
  help: string;
  copiedMessage: string;
}) {
  const { L } = useTexts();
  const toast = useToast();
  const text = useRef<HTMLElement>(null);
  const id = useId();
  return (
    <div className={styles.secret} data-part="dato-una-vez">
      <span className={styles.secretLabel} id={`${id}-label`} data-part="etiqueta">
        {label}
      </span>
      <div className={styles.secretRow}>
        <code ref={text} className={styles.secretValue} aria-labelledby={`${id}-label`} data-part="dato">
          {value}
        </code>
        <button
          type="button"
          className="tm-btn secondary"
          data-part="copiar"
          onClick={() =>
            void copyText(value).then((done) => {
              if (done) toast.show(copiedMessage);
              else {
                selectContents(text.current);
                toast.show(L("ADM_COPY_FAILED"));
              }
            })
          }
        >
          <ChatIcon name="copy" />
          {L("ADM_COPY")}
        </button>
      </div>
      <span className={styles.secretHelp} data-part="ayuda">
        {help}
      </span>
    </div>
  );
}

/**
 * C54 · contraseña o clave de API: el campo de C05 con el ojo adentro (eye «Mostrar» / eye-off «Ocultar», aria-pressed).
 * El foco va en el campo entero; una clave guardada nunca vuelve: el campo arranca vacío.
 */
export function PasswordField({
  id,
  label,
  value,
  onChange,
  autoComplete,
  error,
  help,
  required,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  autoComplete: "current-password" | "new-password";
  error?: string | null;
  help?: string;
  required?: boolean;
}) {
  const { L } = useTexts();
  const [visible, setVisible] = useState(false);
  const toggle = L(visible ? "LOGIN_HIDE_PASSWORD" : "LOGIN_SHOW_PASSWORD");
  const described = [error ? `${id}-error` : null, help ? `${id}-help` : null].filter(Boolean).join(" ") || undefined;
  return (
    <div className={`tm-field${error ? " invalid" : ""}`} data-part="campo-contrasena">
      <label htmlFor={id}>{label}</label>
      <span className={styles.password} data-part="campo">
        <input
          id={id}
          type={visible ? "text" : "password"}
          value={value}
          autoComplete={autoComplete}
          autoCapitalize="off"
          spellCheck={false}
          required={required}
          aria-invalid={error ? "true" : undefined}
          aria-describedby={described}
          className={visible ? styles.passwordVisible : undefined}
          onChange={(event) => onChange(event.target.value)}
        />
        <button
          type="button"
          aria-controls={id}
          aria-label={toggle}
          aria-pressed={visible}
          title={toggle}
          data-part="mostrar"
          onClick={() => setVisible((current) => !current)}
        >
          <ChatIcon name={visible ? "eye-off" : "eye"} />
        </button>
      </span>
      {error ? (
        <span id={`${id}-error`} className="tm-field-error">
          {error}
        </span>
      ) : null}
      {help ? (
        <small id={`${id}-help`} className="tm-field-help">
          {help}
        </small>
      ) : null}
    </div>
  );
}

/** `actions`: lo que la fila ofrece además de subir, bajar y quitar; en el celular va en la hoja de su ⋮ (E103). */
export type ChainRow = {
  key: string;
  name: string;
  line?: ReactNode;
  state?: ReactNode;
  actions?: { label: string; icon: string; run: () => void }[];
};

/**
 * C50 · lista cuyo orden decide algo (qué modelo responde primero y cuáles después). Arrastrar, ↑ ↓ o Alt + ↑/↓; la primera
 * no sube y la última no baja; nunca queda vacía. Cada cambio lo aplica quien la usa (al instante, con «Deshacer»). En el
 * celular, ↑ ↓ y «Quitar» pasan a ⋮ con su hoja.
 */
export function Chain({
  rows,
  onMove,
  onRemove,
  busy,
}: {
  rows: ChainRow[];
  onMove: (from: number, to: number) => void;
  onRemove?: (index: number) => void;
  busy?: boolean;
}) {
  const { L } = useTexts();
  const [dragging, setDragging] = useState<number | null>(null);
  const [over, setOver] = useState<number | null>(null);
  const [menu, setMenu] = useState<number | null>(null);
  const position = (index: number) => (index === 0 ? L("ADM_CHAIN_MAIN") : formatText(L("ADM_CHAIN_BACKUP"), index));
  const move = (from: number, to: number) => {
    if (busy || to < 0 || to >= rows.length || from === to) return;
    onMove(from, to);
  };
  return (
    <ol className={styles.chain} data-part="cadena">
      {rows.map((row, index) => (
        <li
          key={row.key}
          className={`${styles.chainRow}${dragging === index ? ` ${styles.chainDragging}` : ""}${
            over === index && dragging !== null && dragging !== index ? ` ${styles.chainOver}` : ""
          }`}
          draggable={!busy && rows.length > 1}
          data-part="fila"
          onDragStart={(event) => {
            setDragging(index);
            event.dataTransfer.effectAllowed = "move";
          }}
          onDragOver={(event) => {
            if (dragging === null) return;
            event.preventDefault();
            setOver(index);
          }}
          onDragLeave={() => setOver((current) => (current === index ? null : current))}
          onDrop={(event) => {
            event.preventDefault();
            if (dragging !== null) move(dragging, index);
            setDragging(null);
            setOver(null);
          }}
          onDragEnd={() => {
            setDragging(null);
            setOver(null);
          }}
          onKeyDown={(event) => {
            if (!event.altKey) return;
            if (event.key === "ArrowUp") {
              event.preventDefault();
              move(index, index - 1);
            } else if (event.key === "ArrowDown") {
              event.preventDefault();
              move(index, index + 1);
            }
          }}
        >
          <span className={styles.grip} aria-hidden="true" data-part="asa">
            <ChatIcon name="grip-vertical" />
          </span>
          <span className={`${styles.position}${index === 0 ? ` ${styles.positionMain}` : ""}`} data-part="posicion">
            {position(index)}
          </span>
          <span className={styles.chainText}>
            <span className={styles.chainLine1}>
              <span className={styles.chainName}>{row.name}</span>
              {row.state}
            </span>
            {row.line ? <span className={styles.chainLine2}>{row.line}</span> : null}
          </span>
          <span className={styles.chainActions}>
            <button
              type="button"
              className={styles.chainArrow}
              aria-label={`${L("ADM_CHAIN_UP")}: ${row.name}`}
              title={L("ADM_CHAIN_UP")}
              disabled={busy || index === 0}
              onClick={() => move(index, index - 1)}
            >
              <ChatIcon name="arrow-up" />
            </button>
            <button
              type="button"
              className={styles.chainArrow}
              aria-label={`${L("ADM_CHAIN_DOWN")}: ${row.name}`}
              title={L("ADM_CHAIN_DOWN")}
              disabled={busy || index === rows.length - 1}
              onClick={() => move(index, index + 1)}
            >
              <ChatIcon name="arrow-down" />
            </button>
            {onRemove ? (
              <button
                type="button"
                className={`tm-btn secondary ${styles.chainRemove}`}
                disabled={busy || rows.length === 1}
                title={rows.length === 1 ? L("ADM_CHAIN_LAST") : undefined}
                data-part="quitar"
                onClick={() => onRemove(index)}
              >
                {L("ADM_CHAIN_REMOVE")}
              </button>
            ) : null}
          </span>
          <button
            type="button"
            className={styles.chainMore}
            aria-label={`${L("ADM_MORE_ACTIONS")}: ${row.name}`}
            aria-haspopup="dialog"
            disabled={busy}
            onClick={() => setMenu(index)}
          >
            <ChatIcon name="ellipsis-vertical" />
          </button>
        </li>
      ))}
      {menu !== null ? (
        <ChainSheet
          name={rows[menu]?.name ?? ""}
          canUp={menu > 0}
          canDown={menu < rows.length - 1}
          canRemove={!!onRemove && rows.length > 1}
          actions={rows[menu]?.actions ?? []}
          onClose={() => setMenu(null)}
          onUp={() => move(menu, menu - 1)}
          onDown={() => move(menu, menu + 1)}
          onRemove={() => onRemove?.(menu)}
        />
      ) : null}
    </ol>
  );
}

/** Hoja del ⋮ de una fila de la cadena en el celular: «Subir», «Bajar» y «Quitar», con ícono y texto. */
function ChainSheet({
  name,
  canUp,
  canDown,
  canRemove,
  actions,
  onClose,
  onUp,
  onDown,
  onRemove,
}: {
  name: string;
  canUp: boolean;
  canDown: boolean;
  canRemove: boolean;
  actions: { label: string; icon: string; run: () => void }[];
  onClose: () => void;
  onUp: () => void;
  onDown: () => void;
  onRemove: () => void;
}) {
  const { L } = useTexts();
  const item = (label: string, icon: string, run: () => void, enabled: boolean) =>
    enabled ? (
      <button
        type="button"
        className={styles.sheetItem}
        onClick={() => {
          onClose();
          run();
        }}
      >
        <ChatIcon name={icon} />
        {label}
      </button>
    ) : null;
  return (
    // C27 1.14.9 · la hoja compartida (RC1 · K1).
    <Sheet label={name} onClose={onClose}>
      <strong className={styles.sheetTitle}>{name}</strong>
      {item(L("ADM_CHAIN_UP"), "arrow-up", onUp, canUp)}
      {item(L("ADM_CHAIN_DOWN"), "arrow-down", onDown, canDown)}
      {item(L("ADM_CHAIN_REMOVE"), "x", onRemove, canRemove)}
      {actions.map((action) => (
        <Fragment key={action.label}>{item(action.label, action.icon, action.run, true)}</Fragment>
      ))}
    </Sheet>
  );
}
